import React, { useContext } from "react";
import { motion } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FaFacebookF, FaTwitter, FaInstagram, FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from "react-icons/fa";
import { CanvasContext } from "../../contexts/CanvasContext";
import "./LandingAbout.css";

// Import images
import PrinceImage from "../../asset/Prince_Gondaliya.png";
// import NirjalImage from "../../asset/Nirjal_Ambaliya.jpg";
// import VaidikImage from "../../asset/Vaidik_Ghelani.jpg";
// import DarshanImage from "../../asset/Darshan_Finaviya.jpg";
import NexGen from "../../asset/nexGenBlue.gif";

const initialValues = {
  name: "",
  email: "",
  message: "",
  subject: "general",
};

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  message: Yup.string().required("Message is required"),
  subject: Yup.string()
    .oneOf(["general", "support", "feedback"], "Invalid subject")
    .required("Subject is required"),
});

const LandingAbout = () => {
  const { isDarkTheme } = useContext(CanvasContext);

  const onSubmit = async (values, formik) => {
    console.log(values);
    // Handle form submission
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <section
      id="about"
      className={`landing-about ${isDarkTheme ? "dark-theme" : ""}`}
    >
      <div className="landing-about-container">
        <motion.section
          className="hero-section"
          initial="hidden"
          animate="visible"
          variants={sectionVariants}
        >
          <h1>Welcome to NexGen Sites</h1>
          <p>Empowering Your Web Presence</p>
        </motion.section>

        <motion.section
          className="company-story"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <div className="story-content">
            <div className="story-content-text">
              <h2>Our Story</h2>
              <p>
                Our journey began with a simple yet powerful idea: to
                democratize web development. We envisioned a platform where
                creativity meets technology, enabling everyone to build stunning
                websites effortlessly.
              </p>
            </div>
            <div className="story-image">
              <img src={NexGen} alt="Team" />
            </div>
          </div>
        </motion.section>

        <motion.section
          className="mission-vision"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <h2>Mission and Vision</h2>
          <div className="mission-content">
            <div className="mission-card">
              <h3>Mission</h3>
              <p>
                To provide intuitive, powerful, and accessible web development
                tools for all.
              </p>
            </div>
            <div className="mission-card">
              <h3>Vision</h3>
              <p>
                A world where everyone can bring their digital ideas to life,
                regardless of technical background.
              </p>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="core-values"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <h2>Our Core Values</h2>
          <div className="values-grid">
            {[
              "Innovation",
              "User-centric design",
              "Accessibility",
              "Quality",
              "Continuous improvement",
            ].map((value, index) => (
              <div key={index} className="value-item">
                <div className="value-icon">
                  {["🚀", "👥", "🌐", "💎", "📈"][index]}
                </div>
                <h3>{value}</h3>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          className="team-introduction"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <h2>Faces Behind NexGen</h2>
          <div className="team-members">
            {[
              { name: "Prince Gondaliya", image: PrinceImage },
              // { name: "Ghelani Vaidik", image: VaidikImage },
              // { name: "Ambaliya Nirjal", image: NirjalImage },
              // { name: "Finaviya Darshan", image: DarshanImage },
            ].map((member, index) => (
              <div key={index} className="team-member">
                <img src={member.image} alt={member.name} />
                <h3>{member.name}</h3>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          className="unique-selling-points"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <h2>Why Choose NexGen Sites?</h2>
          <div className="usp-grid">
            {[
              {
                title: "User-friendly Interface",
                description:
                  "Intuitive drag-and-drop functionality for effortless design.",
              },
              {
                title: "Customizable Templates",
                description:
                  "A wide range of professionally designed templates to start from.",
              },
              {
                title: "Real-time Collaboration",
                description:
                  "Work together seamlessly with your team in real-time.",
              },
              {
                title: "SEO-friendly",
                description:
                  "Built-in SEO tools to boost your online visibility.",
              },
            ].map((usp, index) => (
              <div key={index} className="usp-item">
                <h3>{usp.title}</h3>
                <p>{usp.description}</p>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          className="testimonials"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <h2>What Our Users Say</h2>
          <div className="testimonial-carousel">
            <div className="testimonial-item">
              <p>
                "NexGen Sites transformed our online presence. It's incredibly
                easy to use!"
              </p>
              <h4>- Sarah J., Small Business Owner</h4>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="future-plans"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <h2>The Future is Bright</h2>
          <p>
            We're constantly innovating to bring you the latest in web
            development technology. Stay tuned for exciting new features that
            will revolutionize your web creation experience!
          </p>
        </motion.section>

        <motion.section
          className="contact-information"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <h2>Get in Touch</h2>
          <p className="section-subtitle">We'd love to hear from you. Drop us a line!</p>
          <div className="contact-wrapper">
            <div className="contact-form-container">
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {(formik) => (
                  <Form className="contact-form">
                    <div className="form-group">
                      <label htmlFor="name">Name</label>
                      <Field type="text" name="name" />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="error"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email</label>
                      <Field type="email" name="email" />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="error"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="subject">Subject</label>
                      <Field as="select" name="subject">
                        <option value="general">General Inquiry</option>
                        <option value="support">Support</option>
                        <option value="feedback">Feedback</option>
                      </Field>
                      <ErrorMessage
                        name="subject"
                        component="div"
                        className="error"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="message">Message</label>
                      <Field as="textarea" name="message" />
                      <ErrorMessage
                        name="message"
                        component="div"
                        className="error"
                      />
                    </div>
                    <div className="form-group">
                      <button
                        type="submit"
                        disabled={!formik.isValid || formik.isSubmitting}
                      >
                        {!formik.isSubmitting ? "Send Message" : "Sending..."}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
            <div className="contact-info">
              <div className="info-item">
                <h3><FaMapMarkerAlt /> Location</h3>
                <p>Silver Trade Center</p>
                <p>Surat, Gujarat, India</p>
              </div>
              <div className="info-item">
                <h3><FaEnvelope /> Contact Details</h3>
                <p>
                  <a href="mailto:princegondaliya20@gmail.com">
                    princegondaliya20@gmail.com
                  </a>
                </p>
                <p><FaPhone /> +91 8347560082</p>
              </div>
              <div className="info-item">
                <h3><FaClock /> Business Hours</h3>
                <p>Monday - Friday: 9 AM - 6 PM</p>
                <p>Response time: Within 24 hours</p>
              </div>
              <div className="info-item">
                <h3>Connect With Us</h3>
                <div className="social-icons">
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                  >
                    <FaFacebookF />
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Twitter"
                  >
                    <FaTwitter />
                  </a>
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                  >
                    <FaInstagram />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="map"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <h2>Visit Our Office</h2>
          <p>Come say hello at our office headquarters</p>
          <iframe
            title="STC, Surat Gujarat"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2852.8516723659927!2d72.86806076829943!3d21.232887638669023!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04f7adffdd9f9%3A0x8e5947b84d098fd9!2sSilver%20Trade%20Center!5e0!3m2!1sen!2sin!4v1723528272020!5m2!1sen!2sin"
            width="600"
            height="450"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </motion.section>
      </div>
    </section>
  );
};

export default LandingAbout;
